import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate, useLocation, useParams } from 'react-router-dom';

import { ReactComponent as SlashIcon } from '../../../../../assets/icons/slash.svg';
import { ReactComponent as StopIcon } from '../../../../../assets/icons/stop-circle.svg';
import DefaultApiLimit from '../../../../../config/defaultLimit';
import { CommercialSessionInterface } from '../../../../../interface/charging-session.interface';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import {
  disableDeviceDetailsSessionFiltersApplied as disableSessionFiltersApplied,
  fetchDeviceDetailsChargingSessions as fetchAllSessions,
  fetchDeviceDetailsListSessionFilters as fetchListSessionFilters,
  updateDeviceDetailsSessionFilters as updateSessionFilters,
  setDeviceDetailsSessionSortData as setSessionSortData,
  refreshDeviceDetailsChargingSessions as refreshChargingSessions,
} from '../../../../../redux/slices/DeviceDetailsChargingSessionSlice';
import { setSelectedDevice } from '../../../../../redux/slices/DeviceManagementSlice';
import { setNotificationMessage } from '../../../../../redux/slices/NotificationManagementSlice';
import { setSelectedStation } from '../../../../../redux/slices/StationManagementSidePanelSlice';
import CustomActionBar from '../../../../../shared-components/custom-action-bar/CustomActionBar';
import CustomTable from '../../../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../../../shared-components/success-notification/SuccessNotification';
import FacilityDetailsSidePanel from '../../../../../shared-components/table-side-panel/FacilityDetailsSidePanel';
import { useTableSidePanel } from '../../../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import StationSidePanel from '../../../../../shared-components/table-side-panel/StationSidePanel';
import { GetParametersFromCommonFilters } from '../../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoDateTime from '../../../../../shared-functions/ConvertUTCtoDateTime';
import { getDaysBetween } from '../../../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../../../shared-functions/ExportFile';
import { permissionCheck } from '../../../../../shared-functions/JWTDocodeFunction';
import occpEvent from '../../../../../utils/api/ocpp-events';

/**
 * Commercial Session Table Component cloned specifically for device details view.
 * @returns {any} The rendered component.
 */
function CommercialSessionTable() {
  const { t, i18n } = useTranslation(['transaction', 'sidePanel', 'account']);
  const dispatch = useAppDispatch();
  const { id: chargingPoints } = useParams();

  /**
   * Format duration in seconds to h m s format
   * @param {number | undefined | null} seconds - The duration in seconds
   * @returns {string} The formatted duration string
   */
  const formatDuration = (seconds: number | undefined | null): string => {
    if (seconds === undefined || seconds === null || seconds <= 0) {
      return '-';
    }
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;

    const result: string[] = [];
    if (h > 0) result.push(`${h}h`);
    if (m > 0) result.push(`${m}m`);
    if (s > 0 || result.length === 0) result.push(`${s}s`);

    return result.join(' ');
  };
  // @ts-ignore
  const {
    payments: sessions,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
    // @ts-ignore
  } = useAppSelector((state) => state.deviceDetailsChargingSession);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { deviceDetails } = useAppSelector((state) => state.tableSidePanel);
  const { selectedChargingPoint } = useAppSelector((state) => state.device);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const isStatusClicked = React.useRef(false);

  const chargerSn =
    selectedChargingPoint?.chargingPointSn ||
    (chargingPoints?.startsWith('SN') ? chargingPoints : '');

  /**
   * Fetch the session details in redux
   * @param {any} data - The data
   */
  const fetchSessions = (data: any) => {
    if (getDaysBetween(data.startDate, data.endDate) > 30) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitMessage'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }

    if (!loading) {
      const extraParameters = {
        'chargingPoints[]': chargerSn || chargingPoints,
        status: 'inProgress',
      };
      dispatch(fetchAllSessions({ ...data, ...extraParameters }));
    }
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchSessions({ ...queryParameters, page, limit, ...sortValue });
  };

  /**
   * Function to open device details view
   * @param {any} _ - The _
   * @param {any} newTab - The newTab
   */
  const handleOpenModelView = (_: any, newTab: boolean) => {
    if (deviceDetails) {
      dispatch(
        setSelectedDevice({
          id: deviceDetails?.id,
          name: deviceDetails?.chargingPointName,
          chargingPointSn: deviceDetails?.chargingPointSn,
          modelSeries: deviceDetails?.modelSeries,
        }),
      );

      const sourcePath =
        location.state?.source || window.location.pathname + window.location.search;
      if (newTab) {
        window.open(
          `/ev-admin/charger-management/${deviceDetails.id}/device-details?searchBy=${deviceDetails.chargingPointName}`,
          '_blank',
        );
      } else {
        navigate(
          `/ev-admin/charger-management/${deviceDetails.id}/device-details?searchBy=${deviceDetails.chargingPointName}`,
          { state: { source: sourcePath } },
        );
      }
    }
  };

  /**
   * Function to open station details view
   * @param {any} chargingStationDate - The chargingStationDate
   * @param {any} newTab - The newTab
   */
  const handleOpenModelStationView = (chargingStationDate: any, newTab: boolean) => {
    if (chargingStationDate) {
      dispatch(
        setSelectedStation({
          id:
            chargingStationDate?.chargingStation ||
            chargingStationDate?.chargingStationId ||
            chargingStationDate?._id,
          name: chargingStationDate?.chargingStationName || chargingStationDate?.name,
        }),
      );

      if (newTab) {
        const url = `/ev-admin/station-expanded/${chargingStationDate.chargingStation || chargingStationDate.chargingStationId || chargingStationDate._id}/station-dashboard?searchBy=${chargingStationDate.chargingStationName || chargingStationDate.name}&source=${location.pathname}${location.search}`;
        window.open(url, '_blank');
      } else {
        navigate(
          {
            pathname: `/ev-admin/station-management/${chargingStationDate.chargingStation || chargingStationDate.chargingStationId || chargingStationDate._id}/station-dashboard`,
            search: `?searchBy=${chargingStationDate.chargingStationName || chargingStationDate.name}`,
          },
          { state: { source: location.pathname + location.search } },
        );
      }
    } else {
      console.log('Error while opening device in separate page');
    }
  };

  const {
    isEditMode,
    openStatus,
    setOpenStatus,
    modalView,
    setModalView,
    handleEditModeChange,
    handleSidePanelClose,
    renderSidePanelTopLeftContent,
  } = useTableSidePanel({
    onRefresh: handleRefresh,
    onOpenStationDetails: handleOpenModelStationView,
    onOpenDeviceDetails: handleOpenModelView,
    sessions,
  });

  /**
   * Modal box close function
   * @param {any} message - The message
   */
  const showSuccessNotification = (message: string) => {
    if (message) {
      setOpenStatus(true);
      setModalView({
        title: '',
        header: false,
        content: (
          <SuccessNotification
            title={message}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setOpenStatus(false);
            }}
          />
        ),
        rightCorner: '',
        notification: true,
      });
    } else {
      setOpenStatus(false);
    }
  };

  /**
   * Function to auto refresh
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      refreshChargingSessions({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
        'chargingPoints[]': chargerSn || chargingPoints,
        status: 'inProgress',
      }),
    );
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    {
      label: t('chargingStartedAt'),
      accessorKey: 'transactionStartsAt',
    },
    {
      label: t('chargingEndedAt'),
      accessorKey: 'transactionEndsAt',
    },
    {
      label: t('accountId'),
      accessorKey: 'accountCustomId',
    },
    {
      label: t('transactionId'),
      accessorKey: 'transactionId',
    },
    {
      label: t('tariffId'),
      accessorKey: 'tariff.tariffId',
    },
    {
      label: t('ocppTransactionId'),
      accessorKey: 'ocppTransactionId',
    },
    {
      label: t('stationName'),
      accessorKey: 'chargingStation',
    },
    {
      label: t('userName'),
      accessorKey: 'endConsumer',
    },
    {
      label: t('duration'),
      accessorKey: 'durationSeconds',
    },
    {
      label: t('status'),
      accessorKey: 'status',
    },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Function to export data in PDF or CSV format
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    const headers = exportCurrentColumns.map((col) => col.label);

    const rows = sessions.map((row: CommercialSessionInterface) =>
      exportCurrentColumns.map((col) => {
        switch (col.accessorKey) {
          case 'transactionStartsAt':
            return ConvertUTCtoDate(row.transactionStartsAt as any);

          case 'transactionEndsAt':
            return ConvertUTCtoDate(row.transactionEndsAt as any);

          case 'status':
            return row.status ? t(row.status) : t('na');

          case 'durationSeconds':
            return formatDuration(row.durationSeconds);

          default:
            return (row as any)[col.accessorKey] || 'N/A';
        }
      }),
    );

    if (rows.length > 0) {
      if (type === 'csv') {
        exportCSV(headers, rows, 'commercial-sessions.csv');
      } else {
        exportPDF(headers, rows, 'commercial-sessions.pdf');
      }
    }
  };

  /**
   * Function to handle stop button click
   * @param {any} data session data or list of ids
   */
  const handleStop = (data: CommercialSessionInterface | string[]) => {
    let itemsToProcess: CommercialSessionInterface[] = [];

    if (Array.isArray(data)) {
      itemsToProcess = sessions.filter((session: any) => data.includes(session.id));
    } else {
      itemsToProcess = [data];
    }

    if (itemsToProcess.length === 0) return;

    const promises = itemsToProcess.map((item) => {
      const payload = {
        chargingPointSn: item.chargingPointSn,
        transactionId: item.transactionId,
      };
      return occpEvent.remoteStopTransaction(payload);
    });

    Promise.all(promises)
      .then(() => {
        showSuccessNotification(
          itemsToProcess.length > 1
            ? t('transactionsStopRequestSuccess')
            : t('transactionStopRequestSuccess'),
        );
        handleRefresh();
        if (Array.isArray(data)) {
          setSelectedRows([]);
        }
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('errorMessage'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  /**
   * Function to handle force stop button click
   * @param {any} data session data or list of ids
   */
  const handleForceStop = (data: CommercialSessionInterface | string[]) => {
    let itemsToProcess: CommercialSessionInterface[] = [];

    if (Array.isArray(data)) {
      itemsToProcess = sessions.filter((session: any) => data.includes(session.id));
    } else {
      itemsToProcess = [data];
    }

    if (itemsToProcess.length === 0) return;

    const promises = itemsToProcess.map((item) => {
      const payload = {
        chargingPointSn: item.chargingPointSn,
        transactionId: item.transactionId,
        forceStop: true,
      };
      return occpEvent.remoteStopTransaction(payload);
    });

    Promise.all(promises)
      .then(() => {
        showSuccessNotification(
          itemsToProcess.length > 1
            ? t('transactionsForceStopRequestSuccess')
            : t('transactionForceStopRequestSuccess'),
        );
        handleRefresh();
        if (Array.isArray(data)) {
          setSelectedRows([]);
        }
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('errorMessage'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  const column = [
    {
      label: t('Session Id'),
      accessorKey: 'transactionId' as keyof CommercialSessionInterface,
    },

    {
      label: t('sessionStartTime'),
      accessorKey: 'transactionStartsAt' as keyof CommercialSessionInterface,
      sortAllow: true,
      /**
       * Custom data for the charging started at column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => (
        <span>{ConvertUTCtoDateTime(rowData?.transactionStartsAt as any)}</span>
      ),
    },
    {
      label: t('SessionEndTime'),
      accessorKey: 'transactionEndsAt' as keyof CommercialSessionInterface,
      sortAllow: true,
      /**
       * Custom data for the charging ended at column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => (
        <span>{ConvertUTCtoDateTime(rowData?.transactionEndsAt as any)}</span>
      ),
    },
    {
      label: t('PartnerName'),
      accessorKey: 'facilityAccountName' as keyof CommercialSessionInterface,
      queryParamsKey: 'accountCustomId',
      queryParamsValue: 'accountCustomId',
      sidePanelComponent: (
        <FacilityDetailsSidePanel
          actionActive
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          fetchIdFrom="queryParams"
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
    },

    {
      label: t('stationName'),
      accessorKey: 'chargingStation' as keyof CommercialSessionInterface,
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStationId',
      sidePanelComponent: (
        <StationSidePanel
          fetchIdFrom="queryParams"
          initialIsEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
    },
    {
      label: t('tariffId'),
      accessorKey: 'tariff' as keyof CommercialSessionInterface,
      /**
       * Custom data for the tariff ID column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => (
        <span>{rowData?.tariff?.tariffId || '-'}</span>
      ),
    },
    {
      label: t('ocppTransactionId'),
      accessorKey: '_id' as keyof CommercialSessionInterface,
    },
    {
      label: t('powerConsumed'),
      accessorKey: 'powerConsumedInKwh' as keyof CommercialSessionInterface,
      /**
       * Custom data for the power consumed column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => (
        <span>
          {rowData.powerConsumedInKwh ? Number(rowData.powerConsumedInKwh).toFixed(3) : ''}
        </span>
      ),
    },
    {
      label: t('duration'),
      accessorKey: 'durationSeconds' as keyof CommercialSessionInterface,
      /**
       * Custom data for the duration column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => (
        <span>{formatDuration(rowData.durationSeconds) || '-'}</span>
      ),
    },
    {
      label: t('status'),
      accessorKey: 'status' as keyof CommercialSessionInterface,
      /**
       * Custom data for the status column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => {
        let statusClass = '';
        if (rowData?.status) {
          const statusLower = rowData.status.toLowerCase();
          if (statusLower === 'finished') {
            statusClass = 'status-finished';
          } else if (
            statusLower === 'inprogress' ||
            statusLower === 'in progress' ||
            statusLower === 'in_progress'
          ) {
            statusClass = 'status-inProgress';
          } else if (statusLower === 'pending') {
            statusClass = 'status-pending';
          } else if (statusLower === 'terminated') {
            statusClass = 'status-terminated';
          } else if (statusLower === 'failed') {
            statusClass = 'status-failed';
          }
        }
        return (
          <span className={`status-badge ${statusClass}`}>
            <span className="status-text capitalize">
              {rowData?.status ? t(rowData.status) : t('na')}
            </span>
          </span>
        );
      },
    },
    {
      label: t('actions'),
      accessorKey: 'id' as keyof CommercialSessionInterface,
      sticky: 'right' as const,
      /**
       * Custom data for the actions column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => {
        if (rowData?.status === 'inProgress') {
          return (
            <div className="stop-buttons">
              <span role="presentation" title={t('stop')}>
                <StopIcon onClick={() => handleStop(rowData)} />
              </span>
              <span role="presentation" title={t('forceStop')}>
                <SlashIcon onClick={() => handleForceStop(rowData)} />
              </span>
            </div>
          );
        }
        return <div>{t('na')}</div>;
      },
    },
  ];

  /**
   * Table pagination function
   * @param {any} value page and limit values
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchSessions({ ...value, ...queryParameters, ...sortValue });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSessionSortData(sortData));
    fetchSessions({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  const isFirstMountFilters = React.useRef(true);

  useEffect(() => {
    if (isFirstMountFilters.current) {
      isFirstMountFilters.current = false;
      return;
    }
    if (isFilterUpdated || isStatusClicked.current) {
      isStatusClicked.current = false;
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchSessions({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableSessionFiltersApplied());
    }
  }, [filters]);

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchSessions({
      page: DefaultApiLimit.page,
      limit,
      ...queryParameters,
    });
  }, [chargingPoints, selectedChargingPoint]);

  useEffect(() => {
    if (
      searchParameters.get('stationId') ||
      searchParameters.get('deviceId') ||
      searchParameters.get('facilityId') ||
      searchParameters.get('userCustomId')
    ) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('stationId');
      parameters.delete('deviceId');
      parameters.delete('facilityId');
      parameters.delete('userCustomId');
      setSearchParameters(parameters, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (
      !searchParameters.get('accountCustomId') &&
      !searchParameters.get('stationId') &&
      !searchParameters.get('deviceId')
    ) {
      if (searchParameters.get('action')) {
        const parameters = new URLSearchParams(searchParameters);
        parameters.delete('action');
        setSearchParameters(parameters, { replace: true });
      }
    }
  }, [searchParameters]);

  return (
    <div className="payment-management-wrap" dir={i18n.dir()}>
      <ModalBox
        open={openStatus}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setOpenStatus(false)}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />

      <div className="table-content">
        <CustomTable
          dataKey="CommercialSessions"
          version="v2"
          header={column}
          data={sessions}
          limit={limit}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          serialNo
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={pageNateLimitChange}
          exportDataPdfOrCSV={permissionCheck('cpoSessionExport') ? exportDataPdfOrCSV : undefined}
          sizeChangeButtonRequired
          filters={filters}
          filtersToDispatch={updateSessionFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListSessionFilters}
          hidedColumnNames={[
            'sessionAmount',
            'platformAmount',
            'networkRevenue',
            'sharableRevenue',
          ]}
          onHiddenColumnsChange={setHiddenColumns}
          requiredRefreshToggle
          refreshFunction={handleAutoRefresh}
          autoRefreshEnabled
          disableAutoRefreshToggle
          refreshRequired
          handleRefresh={handleRefresh}
          refershCurrentPages={handleRefresh}
          hoverLabel={t('refresh')}
          checkbox
          selectedRows={selectedRows}
          onSelectedRowsChange={setSelectedRows}
          selectKey="id"
          isRowSelectable={(row) => row.status === 'inProgress'}
          sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
          onSidePanelClose={handleSidePanelClose}
          created
          lastUpdated
          height={320}
        />
        <CustomActionBar
          selectedRows={selectedRows}
          onSelectedRowsChange={setSelectedRows}
          actions={[
            {
              label: t('stop'),
              buttonType: 'danger-outline',
              /**
               * handle stop callback.
               */
              onClick: () => handleStop(selectedRows),
            },
            {
              label: t('forceStop'),
              buttonType: 'error',
              /**
               * handle force stop callback.
               */
              onClick: () => handleForceStop(selectedRows),
            },
          ]}
        />
      </div>
    </div>
  );
}

export default CommercialSessionTable;
